import React, { useState, useEffect } from 'react';
import { Save, Settings, Server, Bell, Shield, Database, Globe, Key, AlertCircle, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { settingsAPI } from '../../services/api';
import '../../styles/pages/admin/SettingsPage.css';
import '../../styles/admin-shared.css';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    siteName: 'Life Up',
    siteDescription: 'Plataforma de suporte para recuperação',
    maintenanceMode: false,
    allowRegistrations: true,
    emailNotifications: true,
    pushNotifications: true,
    maxUploadSize: 10,
    sessionTimeout: 480,
    minPasswordLength: 8,
    requireEmailVerification: true,
    enableTwoFactor: false,
    logLevel: 'info',
    backupFrequency: 'daily',
    OPENAI_API_KEY: '',
    STRIPE_SECRET_KEY: '',
    STRIPE_PUBLISHABLE_KEY: '',
    FIREBASE_SERVER_KEY: '',
    APPLE_PAY_MERCHANT_ID: '',
    GOOGLE_PAY_MERCHANT_ID: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      if (response.data.success) {
        const dbSettings = {};
        response.data.settings.forEach(s => {
          // Tentar converter valores booleanos e números
          let val = s.value;
          if (val === 'true') val = true;
          else if (val === 'false') val = false;
          else if (!isNaN(val) && val !== '') val = Number(val);
          
          dbSettings[s.key] = val;
        });
        setSettings(prev => ({ ...prev, ...dbSettings }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações do servidor.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const settingsArray = Object.keys(settings).map(key => ({
        key,
        value: String(settings[key]),
        description: getSettingDescription(key)
      }));

      const response = await settingsAPI.updateMultiple(settingsArray);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar configurações.' });
    } finally {
      setSaving(false);
    }
  };

  const getSettingDescription = (key) => {
    const descriptions = {
      siteName: 'Nome da plataforma',
      siteDescription: 'Descrição curta da plataforma',
      maintenanceMode: 'Habilita o modo de manutenção',
      OPENAI_API_KEY: 'Chave secreta para integração com ChatGPT',
      STRIPE_SECRET_KEY: 'Chave secreta para integração com Stripe',
      STRIPE_PUBLISHABLE_KEY: 'Chave pública para o app mobile',
      FIREBASE_SERVER_KEY: 'Chave para notificações push Firebase',
      APPLE_PAY_MERCHANT_ID: 'Identificador do Merchant para integração com Apple Pay',
      GOOGLE_PAY_MERCHANT_ID: 'Identificador do Merchant para integração com Google Pay'
    };
    return descriptions[key] || null;
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="settings-page">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Carregando configurações...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="settings-page">
        <div className="page-header">
          <div>
            <h1><Settings size={26} /> Configurações da Plataforma</h1>
            <p>Gerencie configurações gerais e chaves de API do sistema</p>
          </div>
          <button 
            className={`btn btn-primary ${saving ? 'loading' : ''}`} 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : <><Save size={16} /> Salvar Alterações</>}
          </button>
        </div>

        {message.text && (
          <div className={`message-banner ${message.type}`}>
            {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            {message.text}
          </div>
        )}

        <div className="settings-sections">
          {/* API Keys Section - IMPROVED GROUPING */}
          <div className="settings-section">
            <h2><Key size={18} /> Integrações e Chaves de API</h2>
            <p className="section-desc">Configure as credenciais para os serviços externos utilizados pela plataforma.</p>
            
            <div className="settings-subsection">
              <h3><div className="service-badge openai">OpenAI</div> Chat & Inteligência Artificial</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>API Key (Secret Key)</label>
                  <input
                    type="password"
                    placeholder="sk-..."
                    value={settings.OPENAI_API_KEY || ''}
                    onChange={(e) => handleChange('OPENAI_API_KEY', e.target.value)}
                  />
                  <small>Chave principal para autenticação no ChatGPT.</small>
                </div>
                <div className="setting-item">
                  <label>Modelo Selecionado</label>
                  <select 
                    value={settings.OPENAI_MODEL || 'gpt-3.5-turbo'}
                    onChange={(e) => handleChange('OPENAI_MODEL', e.target.value)}
                  >
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Mais rápido)</option>
                    <option value="gpt-4">GPT-4 (Mais inteligente)</option>
                    <option value="gpt-4o">GPT-4o (Recomendado)</option>
                  </select>
                  <small>Modelo de IA que será utilizado no chat.</small>
                </div>
              </div>
            </div>

            <div className="settings-divider"></div>

            <div className="settings-subsection">
              <h3><div className="service-badge stripe">Stripe</div> Pagamentos e Assinaturas</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Stripe Publishable Key</label>
                  <input
                    type="text"
                    placeholder="pk_test_..."
                    value={settings.STRIPE_PUBLISHABLE_KEY || ''}
                    onChange={(e) => handleChange('STRIPE_PUBLISHABLE_KEY', e.target.value)}
                  />
                  <small>Chave pública enviada para o App Mobile e Frontend Web.</small>
                </div>
                <div className="setting-item">
                  <label>Stripe Secret Key</label>
                  <input
                    type="password"
                    placeholder="sk_test_..."
                    value={settings.STRIPE_SECRET_KEY || ''}
                    onChange={(e) => handleChange('STRIPE_SECRET_KEY', e.target.value)}
                  />
                  <small>Usada para processar transações financeiras.</small>
                </div>
                <div className="setting-item">
                  <label>Stripe Webhook Secret</label>
                  <input
                    type="password"
                    placeholder="whsec_..."
                    value={settings.STRIPE_WEBHOOK_SECRET || ''}
                    onChange={(e) => handleChange('STRIPE_WEBHOOK_SECRET', e.target.value)}
                  />
                  <small>Necessário para sincronizar status de pagamento.</small>
                </div>
              </div>
            </div>

            <div className="settings-divider"></div>

            <div className="settings-subsection">
              <h3><div className="service-badge firebase">Firebase</div> Notificações Push</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Firebase Server Key</label>
                  <input
                    type="password"
                    value={settings.FIREBASE_SERVER_KEY || ''}
                    onChange={(e) => handleChange('FIREBASE_SERVER_KEY', e.target.value)}
                  />
                  <small>Chave legada para envio de notificações Cloud Messaging.</small>
                </div>
              </div>
            </div>

            <div className="settings-divider"></div>

            <div className="settings-subsection">
              <h3><div className="service-badge apple">Apple Pay</div> Carteira Digital</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Merchant ID (Apple)</label>
                  <input
                    type="text"
                    placeholder="merchant.com.seuapp"
                    value={settings.APPLE_PAY_MERCHANT_ID || ''}
                    onChange={(e) => handleChange('APPLE_PAY_MERCHANT_ID', e.target.value)}
                  />
                  <small>Identificador do comerciante registrado na Apple.</small>
                </div>
              </div>
            </div>

            <div className="settings-divider"></div>

            <div className="settings-subsection">
              <h3><div className="service-badge google">Google Pay</div> Carteira Digital</h3>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Merchant ID (Google)</label>
                  <input
                    type="text"
                    placeholder="Ex: 01234567890123456789"
                    value={settings.GOOGLE_PAY_MERCHANT_ID || ''}
                    onChange={(e) => handleChange('GOOGLE_PAY_MERCHANT_ID', e.target.value)}
                  />
                  <small>Identificador do comerciante registrado no Google Pay.</small>
                </div>
              </div>
            </div>
          </div>

          {/* General Settings */}
          <div className="settings-section">
            <h2><Globe size={18} /> Configurações Gerais</h2>
            <div className="settings-grid">
              <div className="setting-item">
                <label>Nome do Site</label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => handleChange('siteName', e.target.value)}
                />
              </div>
              <div className="setting-item">
                <label>Descrição</label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => handleChange('siteDescription', e.target.value)}
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* System Settings */}
          <div className="settings-section">
            <h2><Server size={18} /> Sistema</h2>
            <div className="settings-toggles">
              <div className="toggle-item">
                <div>
                  <strong>Modo de Manutenção</strong>
                  <p>Desabilita o acesso de usuários ao sistema</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="toggle-item">
                <div>
                  <strong>Permitir Novos Cadastros</strong>
                  <p>Permite que novos usuários se registrem</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.allowRegistrations}
                    onChange={(e) => handleChange('allowRegistrations', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="settings-section">
            <h2><Bell size={18} /> Notificações</h2>
            <div className="settings-toggles">
              <div className="toggle-item">
                <div>
                  <strong>Notificações por Email</strong>
                  <p>Envia emails para usuários</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="toggle-item">
                <div>
                  <strong>Push Notifications</strong>
                  <p>Envia notificações push para o app</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.pushNotifications}
                    onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
